import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Play, Square, Clock, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";

export default function Shifts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("mine");
  const [active, setActive] = useState(null);
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [workerFilter, setWorkerFilter] = useState("");
  const [staff, setStaff] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [clockInBusy, setClockInBusy] = useState(null);
  const canOversee = user?.role === "admin" || user?.role === "staff";

  const load = useCallback(async () => {
    setLoading(true);
    // Fetch scheduled shifts for the coming 14 days (mine, for peer workers; can be filtered further later).
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const end = new Date(today.getTime() + 14 * 86400000);
    const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const [a, l, c, sched] = await Promise.all([
      api.get("/me/shifts/active"),
      api.get("/me/shifts"),
      api.get("/clients"),
      api.get("/shifts/scheduled", { params: { from_date: fmtDate(today), to_date: fmtDate(end), user_id: user?.id } }).catch(() => ({ data: [] })),
    ]);
    setActive(a.data);
    setRows(l.data);
    setClients(c.data);
    setScheduled((sched.data || []).filter((s) => s.status === "scheduled" && s.user_id === user?.id));
    if (canOversee) {
      const params = workerFilter ? { user_id: workerFilter } : {};
      const [all, sList] = await Promise.all([
        api.get("/shifts", { params }).catch(() => ({ data: [] })),
        api.get("/staff/list").catch(() => ({ data: [] })),
      ]);
      setAllRows(all.data);
      setStaff(sList.data);
    }
    setLoading(false);
  }, [canOversee, workerFilter, user?.id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!active) return;
    const tick = () => setElapsed(Math.floor((Date.now() - new Date(active.clock_in).getTime()) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active]);

  const start = async () => {
    setBusy(true);
    try { await api.post("/me/shifts/clock-in", { notes, client_id: clientId || null }); setNotes(""); setClientId(""); toast.success("Clocked in"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try { await api.post("/me/shifts/clock-out", { notes }); setNotes(""); toast.success("Clocked out"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const canClockIntoShift = (s) => {
    if (s.status !== "scheduled") return false;
    const now = Date.now();
    const st = new Date(s.scheduled_start).getTime();
    const en = new Date(s.scheduled_end).getTime();
    return now >= st - 15 * 60 * 1000 && now <= en;
  };

  const clockIntoScheduled = async (s) => {
    setClockInBusy(s.id);
    try {
      await api.post("/me/shifts/clock-in", { shift_id: s.id });
      toast.success("Clocked in");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setClockInBusy(null); }
  };

  const hms = (secs) => {
    const h = Math.floor(secs / 3600); const m = Math.floor((secs % 3600) / 60); const s = secs % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };
  const totalMinutes = rows.filter((r) => r.duration_minutes).reduce((sum, r) => sum + r.duration_minutes, 0);

  return (
    <div className="fade-in max-w-4xl" data-testid="shifts-page">
      <div className="eyebrow text-njas-sage-hover text-[10px]">Timekeeping</div>
      <h2 className="font-serif text-3xl text-njas-navy mb-6">Shifts</h2>

      {canOversee && (
        <div className="border-b border-[var(--njas-border)] mb-5">
          <div className="flex gap-1">
            <button onClick={() => setTab("mine")} data-testid="shifts-tab-mine" className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === "mine" ? "border-njas-navy text-njas-navy" : "border-transparent text-njas-muted hover:text-njas-navy"}`}>My shifts</button>
            <button onClick={() => setTab("all")} data-testid="shifts-tab-all" className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === "all" ? "border-njas-navy text-njas-navy" : "border-transparent text-njas-muted hover:text-njas-navy"}`}>All shifts (oversight)</button>
          </div>
        </div>
      )}

      {tab === "mine" && (<>
      {!active && scheduled.length > 0 && (
        <div className="rounded-xl bg-white border border-[var(--njas-border)] p-5 mb-6" data-testid="upcoming-shifts-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-njas-sage-hover" />
              <div className="font-serif text-lg text-njas-navy">Your scheduled shifts</div>
            </div>
            <Link to="/dashboard/roster" className="text-xs text-njas-navy underline hover:text-njas-navy-hover">Open roster</Link>
          </div>
          <div className="space-y-2">
            {scheduled.slice(0, 5).map((s) => {
              const canClock = canClockIntoShift(s);
              const start = new Date(s.scheduled_start);
              const isToday = start.toDateString() === new Date().toDateString();
              return (
                <div key={s.id} data-testid={`scheduled-shift-${s.id}`} className="flex items-center gap-3 rounded-lg border border-[var(--njas-border)] bg-njas-bg-alt/40 p-3">
                  <div className="w-16 text-center shrink-0">
                    <div className={`text-[10px] uppercase tracking-wider ${isToday ? "text-njas-sage-hover font-semibold" : "text-njas-muted"}`}>{isToday ? "Today" : start.toLocaleDateString("en-AU", { weekday: "short" })}</div>
                    <div className="text-xs font-medium text-njas-navy">{start.toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-njas-navy">
                      {start.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false })} – {new Date(s.scheduled_end).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false })}
                    </div>
                    <div className="text-xs text-njas-muted truncate">
                      {s.client_id ? <Link className="text-njas-navy hover:underline" to={`/dashboard/clients/${s.client_id}`}>{s.client_name}</Link> : "General shift"}
                      {s.notes && <span> · {s.notes}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => clockIntoScheduled(s)}
                    disabled={!canClock || clockInBusy === s.id}
                    data-testid={`scheduled-clock-in-${s.id}`}
                    title={!canClock ? "You can clock in from 15 min before your shift starts" : "Clock in"}
                    className={`inline-flex items-center gap-1.5 rounded-full text-xs px-4 py-2 ${
                      canClock ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    } disabled:opacity-60`}
                  >
                    {clockInBusy === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Clock in
                  </button>
                </div>
              );
            })}
          </div>
          {scheduled.length > 5 && <div className="text-xs text-njas-muted mt-2">+ {scheduled.length - 5} more upcoming — view roster</div>}
        </div>
      )}

      <div className={`rounded-xl border p-6 mb-6 ${active ? "bg-emerald-50 border-emerald-200" : "bg-white border-[var(--njas-border)]"}`}>
        {active ? (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
              <div className="font-serif text-2xl text-njas-navy">On shift</div>
              <div className="font-mono text-lg text-emerald-700 ml-auto" data-testid="shift-timer">{hms(elapsed)}</div>
            </div>
            <div className="text-sm text-njas-muted mb-3">Started {new Date(active.clock_in).toLocaleString("en-AU")}</div>
            {active.notes && <div className="text-sm text-njas-text mb-3 whitespace-pre-wrap">{active.notes}</div>}
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add end-of-shift notes (optional)…" className={inputCls} data-testid="clock-out-notes" />
            <div className="flex justify-end mt-3">
              <button onClick={stop} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-red-600 text-white px-6 py-2 text-sm hover:bg-red-700 disabled:opacity-60" data-testid="clock-out-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />} Clock out
              </button>
            </div>
          </>
        ) : isAdmin ? (
          <>
            <div className="font-serif text-2xl text-njas-navy mb-1">Ready to start your shift?</div>
            <div className="text-xs text-njas-muted mb-3">Ad-hoc clock-in is admin-only. Staff and peer workers must clock into a scheduled shift.</div>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls} data-testid="clock-in-client-select">
                <option value="">No specific client (general shift)</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Shift notes (optional)…" className={inputCls} data-testid="clock-in-notes" />
            </div>
            <div className="flex justify-end mt-3">
              <button onClick={start} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-6 py-2 text-sm hover:bg-emerald-700 disabled:opacity-60" data-testid="clock-in-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Clock in
              </button>
            </div>
          </>
        ) : scheduled.length === 0 ? (
          <div className="text-center py-8" data-testid="no-shifts-empty">
            <CalendarClock className="w-10 h-10 text-njas-muted/40 mx-auto mb-3" />
            <div className="font-serif text-xl text-njas-navy mb-1">No shifts scheduled</div>
            <div className="text-sm text-njas-muted mb-4">Your admin hasn&apos;t rostered a shift for you yet.<br />Once they do, it&apos;ll appear here and on your roster.</div>
            <Link to="/dashboard/roster" className="inline-flex items-center gap-1.5 text-sm text-njas-navy underline hover:text-njas-navy-hover">
              <CalendarClock className="w-4 h-4" /> View my roster
            </Link>
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="text-sm text-njas-muted">You have upcoming shifts. Clock in from the panel above when your shift starts.</div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="font-serif text-xl text-njas-navy">Shift history</div>
        <div className="text-sm text-njas-muted"><Clock className="inline w-3.5 h-3.5 mr-1" /> Total logged: <strong className="text-njas-navy">{(totalMinutes / 60).toFixed(1)} hrs</strong></div>
      </div>

      <div className="rounded-xl bg-white border border-[var(--njas-border)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-njas-bg-alt text-njas-muted text-xs uppercase tracking-wider">
            <tr><th className="px-4 py-2 text-left">Clock in</th><th className="px-4 py-2 text-left">Clock out</th><th className="px-4 py-2 text-left">Client</th><th className="px-4 py-2 text-right">Duration</th><th className="px-4 py-2 text-left">Notes</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-6 text-center text-njas-muted">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-njas-muted">No shifts yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[var(--njas-border)]" data-testid={`shift-${r.id}`}>
                <td className="px-4 py-2">{new Date(r.clock_in).toLocaleString("en-AU")}</td>
                <td className="px-4 py-2">{r.clock_out ? new Date(r.clock_out).toLocaleString("en-AU") : <span className="text-emerald-700">Active</span>}</td>
                <td className="px-4 py-2 text-njas-muted">{r.client_name || "—"}</td>
                <td className="px-4 py-2 text-right">{r.duration_minutes != null ? `${Math.floor(r.duration_minutes / 60)}h ${r.duration_minutes % 60}m` : "—"}</td>
                <td className="px-4 py-2 text-njas-muted whitespace-pre-wrap">{r.notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>)}

      {tab === "all" && canOversee && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-wider text-njas-muted">Filter by staff</span>
            <select value={workerFilter} onChange={(e) => setWorkerFilter(e.target.value)} className="rounded-lg border border-[var(--njas-border)] bg-white text-sm py-2 px-3" data-testid="all-shifts-worker-filter">
              <option value="">All</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
            </select>
          </div>
          <div className="rounded-xl bg-white border border-[var(--njas-border)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-njas-bg-alt text-njas-muted text-xs uppercase tracking-wider">
                  <tr><th className="px-4 py-2 text-left">Worker</th><th className="px-4 py-2 text-left">Role</th><th className="px-4 py-2 text-left">Client</th><th className="px-4 py-2 text-left">Clock in</th><th className="px-4 py-2 text-left">Clock out</th><th className="px-4 py-2 text-right">Duration</th><th className="px-4 py-2 text-left">Notes</th></tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={7} className="px-4 py-6 text-center text-njas-muted">Loading…</td></tr>}
                  {!loading && allRows.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-njas-muted">No shifts recorded.</td></tr>}
                  {allRows.map((r) => (
                    <tr key={r.id} className="border-t border-[var(--njas-border)]" data-testid={`all-shift-${r.id}`}>
                      <td className="px-4 py-2 font-medium text-njas-navy">{r.user_name}</td>
                      <td className="px-4 py-2 capitalize text-njas-muted">{(r.user_role || "").replace("_", " ")}</td>
                      <td className="px-4 py-2 text-njas-muted">{r.client_id ? <Link className="text-njas-navy hover:underline" to={`/dashboard/clients/${r.client_id}`}>{r.client_name}</Link> : "—"}</td>
                      <td className="px-4 py-2">{new Date(r.clock_in).toLocaleString("en-AU")}</td>
                      <td className="px-4 py-2">{r.clock_out ? new Date(r.clock_out).toLocaleString("en-AU") : <span className="text-emerald-700">Active</span>}</td>
                      <td className="px-4 py-2 text-right">{r.duration_minutes != null ? `${Math.floor(r.duration_minutes / 60)}h ${r.duration_minutes % 60}m` : "—"}</td>
                      <td className="px-4 py-2 text-njas-muted whitespace-pre-wrap max-w-xs truncate" title={r.notes}>{r.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
